/**
 * Small safe expression language for Calculate Field.
 * Supports field names, numbers, + - * / ( ), unary minus, ABS, ROUND, CONCAT.
 * Does not use eval().
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

export type ExpressionNode =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'field'; readonly name: string }
  | { readonly kind: 'unary'; readonly op: '-'; readonly argument: ExpressionNode }
  | {
      readonly kind: 'binary';
      readonly op: '+' | '-' | '*' | '/';
      readonly left: ExpressionNode;
      readonly right: ExpressionNode;
    }
  | {
      readonly kind: 'call';
      readonly name: 'ABS' | 'ROUND' | 'CONCAT';
      readonly args: ReadonlyArray<ExpressionNode>;
    };

export type ParseResult =
  | { readonly ok: true; readonly expression: ExpressionNode }
  | { readonly ok: false; readonly error: string };

export type ApplyFieldResult =
  | { readonly ok: true; readonly rows: VectorExportFeature[] }
  | { readonly ok: false; readonly error: string };

const FUNCTIONS = new Set(['ABS', 'ROUND', 'CONCAT']);

export function parseFieldExpression(source: string): ParseResult {
  const tokens = tokenize(source);
  if (!tokens.ok) return tokens;
  const parser = new Parser(tokens.tokens);
  try {
    const expression = parser.parseExpression();
    parser.expectEof();
    return { ok: true, expression };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Invalid expression' };
  }
}

export function evaluateFieldExpression(
  expression: ExpressionNode,
  properties: Readonly<Record<string, unknown>>,
): unknown {
  switch (expression.kind) {
    case 'number':
      return expression.value;
    case 'field':
      return properties[expression.name];
    case 'unary': {
      const value = asNumber(evaluateFieldExpression(expression.argument, properties));
      return value === null ? null : -value;
    }
    case 'binary': {
      const left = asNumber(evaluateFieldExpression(expression.left, properties));
      const right = asNumber(evaluateFieldExpression(expression.right, properties));
      if (left === null || right === null) return null;
      if (expression.op === '+') return left + right;
      if (expression.op === '-') return left - right;
      if (expression.op === '*') return left * right;
      if (right === 0) return null;
      return left / right;
    }
    case 'call': {
      if (expression.name === 'CONCAT') {
        return expression.args
          .map((arg) => stringify(evaluateFieldExpression(arg, properties)))
          .join('');
      }
      const first = asNumber(evaluateFieldExpression(expression.args[0], properties));
      if (first === null) return null;
      if (expression.name === 'ABS') return Math.abs(first);
      return Math.round(first);
    }
  }
}

export function applyFieldExpression(
  rows: ReadonlyArray<VectorExportFeature>,
  targetField: string,
  source: string,
): ApplyFieldResult {
  const name = targetField.trim();
  if (!name) return { ok: false, error: 'Enter a target field name.' };
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
    return { ok: false, error: 'Field names must start with a letter or underscore.' };
  }
  const parsed = parseFieldExpression(source);
  if (!parsed.ok) return parsed;
  const next = rows.map((row) => ({
    ...row,
    properties: {
      ...(row.properties ?? {}),
      [name]: evaluateFieldExpression(parsed.expression, row.properties ?? {}),
    },
  }));
  return { ok: true, rows: next };
}

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'ident'; value: string }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' | '(' | ')' | ',' }
  | { kind: 'eof' };

function tokenize(source: string): { ok: true; tokens: Token[] } | { ok: false; error: string } {
  const tokens: Token[] = [];
  let i = 0;
  const text = source.trim();
  if (!text) return { ok: false, error: 'Enter an expression.' };
  while (i < text.length) {
    const ch = text[i];
    if (ch === ' ' || ch === '\t' || ch === '\n') {
      i += 1;
      continue;
    }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/' || ch === '(' || ch === ')' || ch === ',') {
      tokens.push({ kind: 'op', value: ch });
      i += 1;
      continue;
    }
    if (isDigit(ch) || (ch === '.' && isDigit(text[i + 1]))) {
      const start = i;
      i += 1;
      while (i < text.length && (isDigit(text[i]) || text[i] === '.')) i += 1;
      const raw = text.slice(start, i);
      const value = Number(raw);
      if (!Number.isFinite(value)) return { ok: false, error: `Invalid number "${raw}".` };
      tokens.push({ kind: 'number', value });
      continue;
    }
    if (isIdentStart(ch)) {
      const start = i;
      i += 1;
      while (i < text.length && isIdentPart(text[i])) i += 1;
      tokens.push({ kind: 'ident', value: text.slice(start, i) });
      continue;
    }
    return { ok: false, error: `Unexpected character "${ch}".` };
  }
  tokens.push({ kind: 'eof' });
  return { ok: true, tokens };
}

class Parser {
  private index = 0;

  constructor(private readonly tokens: readonly Token[]) {}

  parseExpression(): ExpressionNode {
    return this.parseTerm();
  }

  private parseTerm(): ExpressionNode {
    let node = this.parseFactor();
    while (this.matchOp('+') || this.matchOp('-')) {
      const op = (this.previous() as { kind: 'op'; value: '+' | '-' }).value;
      node = { kind: 'binary', op, left: node, right: this.parseFactor() };
    }
    return node;
  }

  private parseFactor(): ExpressionNode {
    let node = this.parseUnary();
    while (this.matchOp('*') || this.matchOp('/')) {
      const op = (this.previous() as { kind: 'op'; value: '*' | '/' }).value;
      node = { kind: 'binary', op, left: node, right: this.parseUnary() };
    }
    return node;
  }

  private parseUnary(): ExpressionNode {
    if (this.matchOp('-')) {
      return { kind: 'unary', op: '-', argument: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): ExpressionNode {
    const token = this.peek();
    if (token.kind === 'number') {
      this.index += 1;
      return { kind: 'number', value: token.value };
    }
    if (token.kind === 'ident') {
      this.index += 1;
      const name = token.value;
      if (this.matchOp('(')) {
        const fn = name.toUpperCase();
        if (!FUNCTIONS.has(fn)) {
          throw new Error(`Unknown function "${name}".`);
        }
        const args: ExpressionNode[] = [];
        if (!this.checkOp(')')) {
          args.push(this.parseExpression());
          while (this.matchOp(',')) args.push(this.parseExpression());
        }
        if (!this.matchOp(')')) throw new Error('Expected ")".');
        if ((fn === 'ABS' || fn === 'ROUND') && args.length !== 1) {
          throw new Error(`${fn} takes one argument.`);
        }
        if (fn === 'CONCAT' && args.length < 1) {
          throw new Error('CONCAT needs at least one argument.');
        }
        return { kind: 'call', name: fn as 'ABS' | 'ROUND' | 'CONCAT', args };
      }
      return { kind: 'field', name };
    }
    if (this.matchOp('(')) {
      const inner = this.parseExpression();
      if (!this.matchOp(')')) throw new Error('Expected ")".');
      return inner;
    }
    throw new Error('Expected a field, number, or "(".');
  }

  expectEof(): void {
    if (this.peek().kind !== 'eof') throw new Error('Unexpected extra tokens.');
  }

  private peek(): Token {
    return this.tokens[this.index] ?? { kind: 'eof' };
  }

  private previous(): Token {
    return this.tokens[this.index - 1] ?? { kind: 'eof' };
  }

  private checkOp(value: string): boolean {
    const token = this.peek();
    return token.kind === 'op' && token.value === value;
  }

  private matchOp(value: string): boolean {
    if (!this.checkOp(value)) return false;
    this.index += 1;
    return true;
  }
}

function isDigit(ch: string | undefined): boolean {
  return Boolean(ch && ch >= '0' && ch <= '9');
}

function isIdentStart(ch: string | undefined): boolean {
  if (!ch) return false;
  return (ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z') || ch === '_';
}

function isIdentPart(ch: string): boolean {
  return isIdentStart(ch) || isDigit(ch);
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
}
